/* Ready to Go showcase — Paolina Roma (Dawn)
   Custom element driving the pr-home-ready-to-go section:
   - client-side tag filter via chips (exact match, comma = OR list)
   - prev/next arrows with card-width step and start/end state
   - arrows shown only when the track is actually scrollable
   Self-initializing, so it survives shopify:section:load in the editor. */

if (!customElements.get('pr-rtg-carousel')) {
  customElements.define(
    'pr-rtg-carousel',
    class PrRtgCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-pr-rtg-track]');
        this.wrap = this.querySelector('[data-pr-rtg-wrap]');
        this.nav = this.querySelector('[data-pr-rtg-nav]');
        this.prevBtn = this.querySelector('[data-pr-rtg-prev]');
        this.nextBtn = this.querySelector('[data-pr-rtg-next]');
        this.chips = Array.prototype.slice.call(this.querySelectorAll('[data-pr-rtg-chip]'));
        this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-pr-rtg-card]'));
        this.status = this.querySelector('[data-pr-rtg-status]');

        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);

        this.chips.forEach((chip) => {
          chip.addEventListener('click', () => this.onChipClick(chip));
        });

        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', () => this.scrollByStep(-1));
        }
        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', () => this.scrollByStep(1));
        }
        if (this.track) {
          this.track.addEventListener('scroll', this.onScroll, { passive: true });
        }

        if (typeof ResizeObserver !== 'undefined' && this.track) {
          this.resizeObserver = new ResizeObserver(this.onResize);
          this.resizeObserver.observe(this.track);
        } else {
          window.addEventListener('resize', this.onResize);
        }

        this.updateEmpty();
        this.toggleControls();
        this.updateScrollState();
      }

      disconnectedCallback() {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        } else {
          window.removeEventListener('resize', this.onResize);
        }
      }

      scrollBehavior() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      }

      onChipClick(chip) {
        if (chip.getAttribute('aria-pressed') === 'true') return;
        this.chips.forEach((c) => c.setAttribute('aria-pressed', 'false'));
        chip.setAttribute('aria-pressed', 'true');
        this.applyFilter((chip.getAttribute('data-tag') || '').toLowerCase());
      }

      applyFilter(tag) {
        this.cards.forEach((card) => {
          if (!tag || tag === '__all__') {
            card.hidden = false;
            return;
          }
          const raw = (card.getAttribute('data-tags') || '').toLowerCase();
          if (!raw) {
            card.hidden = true;
            return;
          }
          const tagList = raw.split('|').map((t) => t.trim());
          const wanted = tag
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean);
          card.hidden = !wanted.some((w) => tagList.indexOf(w) !== -1);
        });
        if (this.track) this.track.scrollTo({ left: 0, behavior: this.scrollBehavior() });
        this.updateEmpty();
        this.updateStatus();
        this.toggleControls();
        this.updateScrollState();
      }

      updateStatus() {
        if (!this.status) return;
        const visible = this.cards.filter((c) => !c.hidden).length;
        if (visible === 0) {
          const empty = this.querySelector('.pr-rtg__empty');
          this.status.textContent = empty ? empty.textContent.trim() : 'No results';
        } else {
          this.status.textContent = visible + (visible === 1 ? ' set shown' : ' sets shown');
        }
      }

      updateEmpty() {
        const visible = this.cards.filter((c) => !c.hidden).length;
        this.setAttribute('data-empty', visible === 0 ? 'true' : 'false');
      }

      updateScrollState() {
        if (!this.track || !this.wrap) return;
        const max = this.track.scrollWidth - this.track.clientWidth;
        const pos = this.track.scrollLeft;
        const isStart = pos <= 2;
        const isEnd = pos >= max - 2;
        this.wrap.setAttribute('data-start', isStart ? 'true' : 'false');
        this.wrap.setAttribute('data-end', isEnd ? 'true' : 'false');
        if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', isStart);
        if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', isEnd);
      }

      canScroll() {
        if (!this.track) return false;
        return this.track.scrollWidth - this.track.clientWidth > 4;
      }

      toggleControls() {
        if (!this.nav) return;
        if (this.canScroll()) {
          this.nav.setAttribute('data-show', '');
        } else {
          this.nav.removeAttribute('data-show');
        }
      }

      step() {
        if (!this.track) return 0;
        const card = this.track.querySelector('[data-pr-rtg-card]:not([hidden])');
        if (!card) return this.track.clientWidth * 0.8;
        const styles = getComputedStyle(this.track);
        const gap = parseFloat(styles.columnGap || styles.gap || 16) || 16;
        return card.getBoundingClientRect().width + gap;
      }

      scrollByStep(direction) {
        if (!this.track) return;
        this.track.scrollBy({ left: direction * this.step(), behavior: this.scrollBehavior() });
      }

      onScroll() {
        window.requestAnimationFrame(() => this.updateScrollState());
      }

      onResize() {
        this.toggleControls();
        this.updateScrollState();
      }
    }
  );
}
